import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUserStatusStore } from '../../store/userStatusStore';
import { SETTABLE_STATUSES } from '../../utils/presenceStatus';
import PresenceBadge from './PresenceBadge';
import './StatusMenu.css';

/**
 * Online / Away / Busy / Invisible picker for the account menu.
 *
 * Maps `SETTABLE_STATUSES`, NOT `STATUSES`. `STATUSES` gained `offline` on
 * 27.08.2026 so that other people's dots can render it; mapping that list here
 * would have quietly grown an "Offline" row in this menu that the server
 * rejects with `invalid_status`.
 *
 * The status is USER-SET and per device. Nothing infers "away": the app's only
 * activity signal is DOM-level and cannot see the user working in another
 * application, and the OS idle hooks are Windows-only — an idle timer would
 * show macOS users as away while they type. See utils/presenceStatus.js.
 *
 * Its own module rather than more JSX in TauriTitleBar.js, which is already
 * long (conventions: add beside, not to).
 *
 * THIS COMPONENT IS THE LIST, NOT THE TRIGGER. It had a row of its own
 * ("● Online ›") which was removed at the owner's request once the status dot
 * on the account-menu avatar became the trigger: two controls for one list,
 * stacked on top of each other, is clutter rather than choice.
 *
 * So `open` is owned by the caller and there is no internal state to disagree
 * with it — the dot's `aria-expanded` and this list cannot drift apart because
 * there is only one flag.
 */
export default function StatusMenu({ open, onOpenChange }) {
  const { t } = useTranslation();
  const status = useUserStatusStore((s) => s.status);
  const setStatus = useUserStatusStore((s) => s.setStatus);

  if (!open) return null;

  return (
    <div className="status-menu">
      <div className="status-menu__list" role="menu">
        {SETTABLE_STATUSES.map((value) => (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={value === status}
              className={`status-menu__option${
                value === status ? ' status-menu__option--active' : ''
              }`}
              onClick={() => {
                setStatus(value);
                onOpenChange?.(false);
              }}
            >
              <PresenceBadge
                status={value}
                avatarSize={32}
                label={t(`presence.${value}`)}
                className="status-menu__swatch"
              />
              <span className="status-menu__label">{t(`presence.${value}`)}</span>
            </button>
        ))}
      </div>
    </div>
  );
}
